// Auto-generated from network-erc-3643.html. Verbatim redesign import. Do NOT edit.
export const MARKUP = `
<header class="page-header">
<canvas aria-hidden="true" class="page-header-canvas" id="netErc3643HeroCanvas"></canvas>
<div class="page-header-veil"></div>
<div class="wrap">
<div class="hero-grid">
<div>
<h1 class="reveal">Making <span class="accent">internet-native securities</span> possible.</h1>
<p class="lede reveal">ERC-3643 makes compliance part of a security's ownership logic: onchain identity, asset-specific eligibility rules and transfer conditions determine whether ownership can change. The result is a new financial primitive: securities that can be distributed, financed, composed and built upon onchain.</p>
<div class="cta-row reveal">
<a class="btn btn-primary" href="/contact">Tokenize with T-REX</a>
<a class="btn btn-ghost" href="https://docs.t-rex.network/" rel="noopener noreferrer" target="_blank">Read the technical docs</a>
</div>
</div>
</div>
</div>
</header>

<section class="section section-day">
<div class="wrap">
<div class="section-head reveal">
<h2 class="head" style="max-width:24ch">Why regulated assets need a <span class="accent">permissioned standard</span>.</h2>
</div>
<div class="grid-3 reveal">
<div class="tile">
<div class="tn">ERC-20 / 721 / 1155</div>
<h3>Permissionless by design</h3>
<p>Standard token formats let any wallet hold and any wallet transfer. The right model for utility tokens, the wrong one for regulated instruments, where who can hold is the whole point.</p>
</div>
<div class="tile">
<div class="tn">The current solution</div>
<h3>Compliance wrapped around the token</h3>
<p>Allow-lists and off-chain gatekeeping sit beside the asset. They have to be re-implemented on every venue and chain, and they fail the moment the asset moves somewhere that does not enforce them.</p>
</div>
<div class="tile tile-win">
<div class="tn">ERC-3643</div>
<h3>Compliance inside the asset</h3>
<p>Eligibility and transfer rules are part of the token contract. Every transfer is checked by the asset itself, so the rules are enforced wherever the token is held, with no external gatekeeper to replicate.</p>
</div>
</div>
<p class="lede reveal" style="margin-top:clamp(30px,4vh,44px);max-width:78ch">DeFi has largely been built around balances, prices and pseudonymous wallets. ERC-3643 assets let applications reason about participants too. That opens the door to eligible liquidity, undercollateralized credit, institutional lending, repo, servicing-aware vaults and markets that were difficult to build around permissionless tokens alone.</p>
</div>
</section>

<section class="section section-night">
<div class="wrap">
<div class="section-head reveal">
<h2 class="head" style="max-width:24ch">How a <span class="accent">permissioned asset</span> works.</h2>
<p class="lede">An ERC-3643 asset combines four capabilities, each written into the token contract itself.</p>
</div>
<div class="anatomy">
<article class="acard reveal">
<span class="anum">01</span><span class="aglow"></span>
<h3>Onchain identity</h3>
<p>The wallet is linked to an identity record carrying claims from trusted issuers. Personal KYC data does not need to be made public.</p>
</article>
<article class="acard reveal">
<span class="anum">02</span><span class="aglow"></span>
<h3>Holder eligibility</h3>
<p>Those claims establish whether the wallet is permitted to hold the asset.</p>
</article>
<article class="acard reveal">
<span class="anum">03</span><span class="aglow"></span>
<h3>Transfer conditions</h3>
<p>Each transaction is checked against rules such as jurisdiction, holding limits and lock-ups.</p>
</article>
<article class="acard reveal">
<span class="anum">04</span><span class="aglow"></span>
<h3>Issuer control</h3>
<p>Authorised agents can issue, freeze, recover, transfer or redeem the asset when required.</p>
</article>
</div>
</div>
</section>

<section class="section section-day">
<div class="wrap">

<figure aria-label="An ERC-3643 transfer clears only when the token's investor and offering rules are satisfied" class="tflow reveal">
<div class="tflow-stage">
<svg id="tflowSvg" preserveaspectratio="xMidYMid meet" viewbox="0 50 1100 440" xmlns="http://www.w3.org/2000/svg">
<defs>
<radialgradient cx="38%" cy="34%" id="tfnode" r="70%">
<stop offset="0" stop-color="#FFFFFF"></stop><stop offset="1" stop-color="#FEFCF8"></stop>
</radialgradient>
</defs>

<rect class="tf-market" height="184" id="tf-box-primary" rx="10" width="1020" x="40" y="80"></rect>
<rect class="tf-market" height="184" id="tf-box-secondary" rx="10" width="1020" x="40" y="276"></rect>
<text class="tf-market-l" text-anchor="middle" transform="rotate(-90 78 172)" x="78" y="172">Primary Market</text>
<text class="tf-market-l" text-anchor="middle" transform="rotate(-90 78 368)" x="78" y="368">Secondary Market</text>

<line class="tf-track" x1="227" x2="400" y1="172" y2="172"></line>
<polygon class="tf-head" points="400,172 388,166 388,178"></polygon>
<text class="tf-alabel" text-anchor="middle" x="313" y="154">Token creation</text>
<line class="tf-track" x1="700" x2="873" y1="172" y2="172"></line>
<polygon class="tf-head" points="873,172 861,166 861,178"></polygon>
<text class="tf-alabel" text-anchor="middle" x="787" y="154">Tokens allocation</text>
<circle class="tf-pkt" cx="227" cy="172" id="tf-pkt-p1" r="5" style="opacity:0"></circle>
<circle class="tf-pkt" cx="700" cy="172" id="tf-pkt-p2" r="5" style="opacity:0"></circle>
<circle cx="165" cy="172" fill="url(#tfnode)" r="52" stroke="#D8D0C2" stroke-width="1.5"></circle>
<text class="tf-node-t" text-anchor="middle" x="165" y="177">Issuers</text>
<circle cx="935" cy="172" fill="url(#tfnode)" r="52" stroke="#D8D0C2" stroke-width="1.5"></circle>
<text class="tf-node-t" text-anchor="middle" x="935" y="177">Investors</text>

<line class="tf-track" x1="227" x2="400" y1="368" y2="368"></line>
<polygon class="tf-head" points="227,368 239,362 239,374"></polygon>
<polygon class="tf-head" points="400,368 388,362 388,374"></polygon>
<text class="tf-alabel" text-anchor="middle" x="313" y="352">Transfer tokens</text>
<text class="tf-alabel" text-anchor="middle" x="313" y="390">Receive money</text>
<line class="tf-track" x1="700" x2="873" y1="368" y2="368"></line>
<polygon class="tf-head" points="700,368 712,362 712,374"></polygon>
<polygon class="tf-head" points="873,368 861,362 861,374"></polygon>
<text class="tf-alabel" text-anchor="middle" x="787" y="352">Transfer money</text>
<text class="tf-alabel" text-anchor="middle" x="787" y="390">Receive tokens</text>
<circle class="tf-pkt" cx="227" cy="368" id="tf-pkt-s1" r="5" style="opacity:0"></circle>
<circle class="tf-pkt" cx="700" cy="368" id="tf-pkt-s2" r="5" style="opacity:0"></circle>
<circle cx="165" cy="368" fill="url(#tfnode)" r="52" stroke="#D8D0C2" stroke-width="1.5"></circle>
<text class="tf-node-t tf-node-sm" text-anchor="middle" x="165" y="362">Investor</text>
<text class="tf-node-t tf-node-sm" text-anchor="middle" x="165" y="382">A</text>
<circle cx="935" cy="368" fill="url(#tfnode)" r="52" stroke="#D8D0C2" stroke-width="1.5"></circle>
<text class="tf-node-t tf-node-sm" text-anchor="middle" x="935" y="362">Investor</text>
<text class="tf-node-t tf-node-sm" text-anchor="middle" x="935" y="382">B</text>

<rect fill="#FEFCF8" height="300" id="tf-gate" rx="14" stroke="#C9BBA0" stroke-width="2" width="300" x="400" y="120"></rect>
<text class="tf-gate-t" id="tf-gate-t" text-anchor="middle" x="550" y="200">ERC-3643</text>
<g class="tf-check" id="tf-check-1">
<rect class="tf-check-box" height="44" rx="7" width="236" x="432" y="262"></rect>
<path class="tf-tick" d="M478 284 l7 7 l13 -15"></path>
<text class="tf-check-t" text-anchor="start" x="508" y="290">Investor rules</text>
</g>
<g class="tf-check" id="tf-check-2">
<rect class="tf-check-box" height="44" rx="7" width="236" x="432" y="318"></rect>
<path class="tf-tick" d="M478 340 l7 7 l13 -15"></path>
<text class="tf-check-t" text-anchor="start" x="508" y="346">Offering rules</text>
</g>
</svg>
</div>
<figcaption class="tflow-cap">Holder identity checked · asset conditions checked · transfer executes or reverts.</figcaption>
</figure>
</div>
</section>

<section class="section section-day">
<div class="wrap">
<div class="section-head reveal">
<h2 class="head" style="max-width:22ch">What ERC-3643 <span class="accent">makes possible</span>.</h2>
<p class="lede" style="max-width:72ch">The world's most important assets become internet-native building blocks. Crypto demonstrated what developers can create with open, programmable assets. ERC-3643 extends that design space to funds, credit, equities, bonds and tokenized cash, the assets that underpin the global economy.</p>
</div>
<div class="grid-3 reveal">
<div class="tile aclass">
<h3>Eligible liquidity</h3>
<p>Pools and order books that check who is on the other side, so regulated assets trade against verified counterparties.</p>
<span aria-hidden="true" class="acl-motif"><svg fill="none" stroke="currentColor" stroke-width="1.4" viewbox="0 0 64 64"><ellipse cx="32" cy="20" rx="18" ry="7"></ellipse><path d="M14 20v12c0 3.9 8 7 18 7s18-3.1 18-7V20"></path><path d="M14 32v12c0 3.9 8 7 18 7s18-3.1 18-7V32"></path></svg></span>
</div>
<div class="tile aclass">
<h3>Credit and collateral</h3>
<p>Tokenized holdings that post as productive collateral in permissioned lending, repo and treasury markets.</p>
<span aria-hidden="true" class="acl-motif"><svg fill="none" stroke="currentColor" stroke-width="1.4" viewbox="0 0 64 64"><path d="M10 44h44"></path><path d="M18 44V30M30 44V20M42 44V26M52 44V14"></path><circle cx="18" cy="30" r="2.5"></circle><circle cx="30" cy="20" r="2.5"></circle><circle cx="42" cy="26" r="2.5"></circle><circle cx="52" cy="14" r="2.5"></circle><path d="M18 30 L30 20 L42 26 L52 14"></path></svg></span>
</div>
<div class="tile aclass">
<h3>Open distribution</h3>
<p>One asset routed to primary and secondary venues, without re-issuing for each venue.</p>
<span aria-hidden="true" class="acl-motif"><svg fill="none" stroke="currentColor" stroke-width="1.4" viewbox="0 0 64 64"><rect height="28" rx="2" width="36" x="14" y="12"></rect><path d="M21 21h22M21 27h22M21 33h14"></path><circle cx="42" cy="46" r="6"></circle><path d="M39 51l-2 7 5-3 5 3-2-7"></path></svg></span>
</div>
<div class="tile aclass">
<h3>Programmable servicing</h3>
<p>Coupons, dividends, corporate actions and reporting that run against the asset's own record.</p>
<span aria-hidden="true" class="acl-motif"><svg fill="none" stroke="currentColor" stroke-width="1.4" viewbox="0 0 64 64"><path d="M10 50h44"></path><rect height="16" width="7" x="16" y="34"></rect><rect height="26" width="7" x="29" y="24"></rect><rect height="34" width="7" x="42" y="16"></rect></svg></span>
</div>
<div class="tile aclass">
<h3>New financial applications</h3>
<p>Products that reason about participants, not just balances, from servicing-aware vaults to institutional credit.</p>
<span aria-hidden="true" class="acl-motif"><svg fill="none" stroke="currentColor" stroke-width="1.4" viewbox="0 0 64 64"><path d="M14 30 L32 16 L50 30"></path><path d="M18 28v22h28V28"></path><rect height="7" width="7" x="23" y="34"></rect><rect height="7" width="7" x="34" y="34"></rect><path d="M28 50v-6h8v6"></path></svg></span>
</div>
</div>
</div>
</section>

<section class="section section-day">
<div class="wrap">
<div class="hero-grid" style="display:grid;grid-template-columns:1fr 1.4fr;gap:clamp(28px,4vw,64px);align-items:center">
<div class="reveal">
<h2 class="head" style="max-width:14ch">Open source. Open audit. <span class="accent">Open</span> to all.</h2>
<p class="lede">ERC-3643 is open under permissive licensing and maintained in public by the ERC3643 Association. Read the spec, fork the contracts, run the tests, build to the same target as every other institution on the standard.</p>
</div>
<div class="devlist reveal">
<a class="devlink" href="https://docs.t-rex.network" rel="noopener noreferrer" target="_blank">
<div class="dl-main"><span class="dl-ico">◆</span><div><div class="dl-label">docs.t-rex.network</div><div class="dl-desc">T-REX developer documentation</div></div></div>
<span class="dl-arrow">→</span>
</a>
<a class="devlink" href="https://docs.erc3643.org" rel="noopener noreferrer" target="_blank">
<div class="dl-main"><span class="dl-ico">◆</span><div><div class="dl-label">docs.erc3643.org</div><div class="dl-desc">The standard's specification</div></div></div>
<span class="dl-arrow">→</span>
</a>
<a class="devlink" href="https://github.com/ERC-3643/ERC-3643" rel="noopener noreferrer" target="_blank">
<div class="dl-main"><span class="dl-ico">◆</span><div><div class="dl-label">github.com/ERC-3643</div><div class="dl-desc">Open-source reference implementations</div></div></div>
<span class="dl-arrow">→</span>
</a>
</div>
</div>
</div>
</section>

<section class="section section-day">
<div class="wrap">
<div class="section-head center reveal">
<h2 class="head">Frequently asked <span class="accent">questions</span>.</h2>
</div>
<div class="faq reveal">
<details class="faq-item"><summary>What does ERC-3643 mean?</summary><div class="faq-a">ERC-3643 is the officially recognised Ethereum token standard for the T-REX Protocol, the name the Ethereum community gave it when it was accepted as a standard.</div></details>
<details class="faq-item"><summary>Is ERC-3643 an alternative to ERC-20?</summary><div class="faq-a">ERC-3643 is built on ERC-20. It inherits the full ERC-20 interface, so the same wallets, exchanges, custodians, and tooling work natively. What it adds is a compliance layer baked into the standard: onchain identity, eligibility checks, and transfer rules enforced at the token contract itself. It is not a choice between the two, ERC-3643 is the regulated form of ERC-20.</div></details>
<details class="faq-item"><summary>Who maintains ERC-3643?</summary><div class="faq-a">The ERC3643 Association: a small, non-profit body of experts. The Association curates the specification, maintains the open-source reference implementations, and runs the working groups that propose changes. The wider community of 140+ institutions consumes and contributes to what the Association publishes.</div></details>
<details class="faq-item"><summary>Is ERC-3643 audited?</summary><div class="faq-a">Yes. The reference contracts have been audited by multiple firms, with public reports. Implementations built on the standard inherit that audit history, then add their own audit on the issuer-specific configuration.</div></details>
<details class="faq-item"><summary>Which blockchains does ERC-3643 support?</summary><div class="faq-a">ERC-3643 is EVM-native and runs on any EVM-compatible chain. It can also be implemented on non-EVM chains using T-REX Lite.</div></details>
<details class="faq-item"><summary>Do I have to use T-REX Network to use ERC-3643?</summary><div class="faq-a">The standard itself is open source, so technically you can deploy an ERC-3643 token on your own. In practice, though, T-REX Network is not something you bolt on after issuance, the Ledger, identity registry, and compliance engine have to govern the asset from the first mint. A standalone deployment stays isolated, it won't share compliance state with the Network, can't travel cross-chain through the Ledger, and isn't reachable through the AppStore's distribution rails. Issuers join the T-REX Network at inception, or stay independent.</div></details>
</div>
</div>
</section>

<section class="section section-night center">
<div class="wrap">
<div class="reveal">
<h2 class="head">Tokenize on the <span class="accent">standard</span> the industry is converging on.</h2>
<p class="lede">Talk to an institutional team that has shipped the standard at scale. We will walk your asset through eligibility, transfer rules, and distribution in one session.</p>
<div class="cta-row" style="margin-top:clamp(30px,4vh,44px);display:flex;flex-wrap:wrap;gap:14px;justify-content:center">
<a class="btn btn-primary" href="/contact">Tokenize with T-REX</a>
<a class="btn btn-ghost" href="https://docs.t-rex.network/" rel="noopener noreferrer" target="_blank">Read the technical docs</a>
</div>
</div>
</div>
</section>
`;
